"use client";
import { useState } from "react";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BookDemoHubspotButtonUrl } from "@/utils/hubspot-contants";
import CustomButton from "../common/CustomButton";

const SearchCalculator = () => {
  const [employees, setEmployees] = useState<number[]>([100]);
  const [averageSalary, setAverageSalary] = useState<string>("50000");
  const [workHours, setWorkHours] = useState<string>("9");

  // Salary options
  const salaryOptions = [
    "30000",
    "40000",
    "50000",
    "60000",
    "70000",
    "80000",
    "90000",
    "100000",
    "120000",
    "150000",
    "200000",
  ];

  // Work hours options
  const workHoursOptions = ["6", "7", "8", "9", "10", "11", "12"];

  // Calculate values
  const timeWastedDaily = Math.round(
    employees[0] * parseFloat(workHours) * 0.3,
  );
  const hourlyRate = parseFloat(averageSalary) / (parseFloat(workHours) * 250);
  const costWastedYearly = Math.round(timeWastedDaily * 250 * hourlyRate);
  const potentialSavings = Math.round(costWastedYearly * 0.5);

  const formatNumber = (num: number) => {
    return num.toLocaleString("en-US");
  };

  const formatCurrency = (num: number) => {
    return `$${num.toLocaleString("en-US")}`;
  };

  return (
    <div className="bg-white px-4 py-12 xl:py-16">
      <div className="container mx-auto px-4">
        <div className="rounded-2xl bg-gradient-to-b to-[#F4F4FD] from-[#ffffff]">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="rounded-2xl p-6">
              <div className="space-y-6">
                <h3 className="text-[18px] font-semibold text-[#202020] xl:text-[22px]">
                  Your Team Details
                </h3>
                <div className="space-y-4">
                  <label className="text-[14px] text-[#202020]">
                    Number of Employees
                  </label>
                  <Slider
                    value={employees}
                    max={500}
                    step={1}
                    onValueChange={(value) => setEmployees(value)}
                    ariaLabel="Number of Employees"
                    className="[&_.cost-progress-bar]:bg-[#6049E7]"
                  />
                  <div className="flex items-center justify-between text-[14px] font-medium text-[#202020]">
                    {[0, 100, 200, 300, 400, 500].map((value) => (
                      <span key={value}>{value}</span>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[14px] text-[#202020]">
                    Average Salary ($)
                  </label>
                  <Select
                    value={averageSalary}
                    onValueChange={setAverageSalary}
                  >
                    <SelectTrigger className="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-base text-[#717171] focus:border-[#6B46C1] focus:outline-none focus:ring-2 focus:ring-[#6B46C1]/20">
                      <SelectValue>
                        ${parseInt(averageSalary).toLocaleString("en-US")}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-[#F6F8FE]">
                      {salaryOptions.map((salary) => (
                        <SelectItem
                          className="text-[14px] text-[#202020]"
                          key={salary}
                          value={salary}
                        >
                          <span className="text-[14px] text-[#202020]">
                            ${parseInt(salary).toLocaleString("en-US")}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-[14px] text-[#202020]">
                    Work Hours Per Day
                  </label>
                  <Select value={workHours} onValueChange={setWorkHours}>
                    <SelectTrigger className="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-base text-[#717171] focus:border-[#6B46C1] focus:outline-none focus:ring-2 focus:ring-[#6B46C1]/20">
                      <SelectValue>{workHours} hours</SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-[#F6F8FE]">
                      {workHoursOptions.map((hours) => (
                        <SelectItem
                          className="text-[14px] text-[#202020]"
                          key={hours}
                          value={hours}
                        >
                          <span className="text-[14px] text-[#202020]">
                            {hours} hours
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="rounded-r-2xl p-6">
              <div className="space-y-4 rounded-2xl">
                <h3 className="text-[18px] font-semibold text-[#202020] xl:text-[22px]">
                  Your Potential Savings
                </h3>
                <div className="space-y-1">
                  <label className="text-[14px] text-[#717171]">
                    Time Wasted Daily
                  </label>
                  <p className="text-[24px] font-bold bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent xl:text-[28px]">
                    {formatNumber(timeWastedDaily)} hours
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-[14px] text-[#717171]">
                    Cost Wasted Yearly
                  </label>
                  <p className="text-[24px] font-bold bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent xl:text-[28px]">
                    {formatCurrency(costWastedYearly)}
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-[14px] text-[#717171]">
                    Potential Annual Savings
                  </label>
                  <p className="text-[24px] font-bold text-[#202020] xl:text-[28px]">
                    {formatCurrency(potentialSavings)}
                  </p>
                  <p className="text-[14px] text-[#717171]">
                    With 50% reduction in search time
                  </p>
                </div>
                <CustomButton
                  text="Get Your Personalized Demo"
                  url={BookDemoHubspotButtonUrl}
                  variant="primary"
                  version="black"
                  textSizeBtnClass="w-fit"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchCalculator;
